import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useBranding } from '@/hooks/useBranding';
import RestaurantLogo from '@/components/branding/RestaurantLogo';
import HalalBadge from '@/components/branding/HalalBadge';
import JustEatButton from '@/components/feature/JustEatButton';

interface NavbarProps {
  transparent?: boolean;
}

const Navbar = ({ transparent = false }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { get } = useSiteSettings();
  const { logoPlacement, restaurantName, logoSize } = useBranding();
  // Dikey logo: markalama genişliğine göre navbar yüksekliği
  const navHeight = Math.max(72, Math.min(Math.round(logoSize * 1.15 + 20), 120));
  const logoMaxHeight = navHeight - 16;
  const halalSize = Math.max(40, Math.min(52, Math.round(logoMaxHeight * 0.62)));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnchorClick = useCallback((e: React.MouseEvent, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.replace('/#', '');
      setMobileOpen(false);
      if (location.pathname === '/') {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
      }
    }
  }, [location.pathname, navigate]);

  const navLinks = [
    { label: 'Speisekarte', href: '/menu' },
    { label: 'Über uns', href: '/#about' },
    { label: 'Kontakt', href: '/#contact' },
  ];

  const isActive = (href: string) => location.pathname === href;

  const bgClass = transparent && !scrolled
    ? 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md border-b border-stone-100';

  const textClass = transparent && !scrolled ? 'text-white' : 'text-stone-800';
  const name = get('restaurant_name', restaurantName);
  const isCentered = logoPlacement === 'center';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="relative w-full" style={{ height: navHeight }}>
        <div
          className={`max-w-7xl mx-auto h-full w-full pl-4 md:pl-8 pr-4 md:pr-16 flex items-center ${isCentered ? 'relative justify-between' : 'justify-between'}`}
        >
          {isCentered && <div className="hidden md:block flex-1" />}

          <Link
            to="/"
            className={`flex items-center flex-shrink-0 ${isCentered ? 'md:absolute md:left-1/2 md:-translate-x-1/2' : ''}`}
          >
            <RestaurantLogo
              maxHeight={logoMaxHeight}
              invert={transparent && !scrolled}
              alt={name}
            />
          </Link>

          <div className={`hidden md:flex items-center gap-8 ${isCentered ? 'flex-1 justify-end' : ''}`}>
            {navLinks.map((link) => {
              const navItem = link.href.startsWith('/#') ? (
                <a
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-amber-500 cursor-pointer ${textClass}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.href}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-amber-500 ${textClass} ${isActive(link.href) ? 'text-amber-500' : ''}`}
                >
                  {link.label}
                </Link>
              );
              return <span key={link.label}>{navItem}</span>;
            })}
          </div>

          <div className="flex items-center gap-3">
            <JustEatButton className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-base"></i>
              </div>
              Jetzt bestellen
            </JustEatButton>

            <HalalBadge size={halalSize} className="md:hidden" />

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${textClass}`}
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        <HalalBadge
          size={halalSize}
          className="hidden md:flex absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10"
        />
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 flex flex-col gap-3">
          {navLinks.map((link) => {
            const navItem = link.href.startsWith('/#') ? (
              <a
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="text-stone-700 font-medium py-2 border-b border-stone-50 text-sm cursor-pointer"
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-stone-700 font-medium py-2 border-b border-stone-50 text-sm"
              >
                {link.label}
              </Link>
            );
            return <span key={link.label}>{navItem}</span>;
          })}
          <JustEatButton
            onClick={() => setMobileOpen(false)}
            className="mt-2 w-full py-3 bg-amber-500 text-white font-semibold rounded-full text-sm cursor-pointer whitespace-nowrap text-center"
          >
            Jetzt bestellen
          </JustEatButton>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
